import { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { falArrowUpFromBracket } from 'shared/@generated/icons/falArrowUpFromBracket';
import { Button } from 'shared/components/button';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import type { AdvisorSharedResult, AdvisorTranslate } from 'shared/library/breed-advisor';
import { useRoute } from 'shared/library/routes/route';
import { share } from 'shared/library/share';
import { notify } from 'shared/library/notify';

export function BreedAdvisorShare({
  createShare,
  copy,
  compact = false,
}: {
  createShare: () => Promise<AdvisorSharedResult | null>;
  copy: AdvisorTranslate;
  compact?: boolean;
}) {
  const route = useRoute();
  const [result, setResult] = useState<AdvisorSharedResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const sharing = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  function getShareUrl(snapshot: AdvisorSharedResult) {
    const path = route(
      'breeds.dogs.advisor.share',
      { share: snapshot.uuid },
      {
        includeDomain: Platform.OS !== 'web',
        preserveTabContext: false,
      },
    );
    return new URL(
      path,
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://getapet.com',
    ).href;
  }
  const url = result ? getShareUrl(result) : null;

  async function shareResult() {
    if (sharing.current) return;
    sharing.current = true;
    setLoading(true);
    setError(false);
    try {
      const snapshot = result ?? (await createShare());
      if (!mounted.current) return;
      if (!snapshot) {
        setError(true);
        return;
      }
      setResult(snapshot);
      const outcome = await share(
        {
          title: copy('share_title'),
          message: copy('share_message'),
          url: getShareUrl(snapshot),
        },
        { content_type: 'breed_advisor' },
      );
      if (!mounted.current) return;
      if (outcome.share_outcome === 'clipboard')
        notify.success({ description: copy('share_copied') });
      setError(outcome.share_outcome === 'failed');
    } catch {
      if (mounted.current) setError(true);
    } finally {
      sharing.current = false;
      if (mounted.current) setLoading(false);
    }
  }

  return (
    <View
      className={compact ? 'items-center gap-2' : 'items-end gap-2'}
      testID="advisor-share-area"
    >
      {!compact && (
        <Text className="text-brand-brown-dark/65 max-w-sm text-right text-xs leading-5">
          {copy('share_privacy')}
        </Text>
      )}
      <Button
        dense
        color="white"
        icon={falArrowUpFromBracket}
        iconPosition="left"
        text={copy(result ? 'share_link' : 'share_results')}
        loading={loading}
        testID="advisor-share"
        onPress={() => void shareResult()}
      />
      {compact && result && (
        <Text className="text-brand-brown-dark/65 max-w-sm text-center text-xs leading-5">
          {copy('share_privacy')}
        </Text>
      )}
      {url && (
        <Link href={url} openInNewTab textClassName="text-brand-brown-dark/70 text-xs underline">
          {copy('share_open')}
        </Link>
      )}
      {error && (
        <Text accessibilityRole="alert" className="text-brand-brown-dark text-xs leading-5">
          {copy('share_error')}
        </Text>
      )}
    </View>
  );
}
